<script setup lang="ts">
import { computed, ref } from 'vue';
import { default as ButtonEl } from '@/components/shared/Button.vue';
import BaseOverlay from '@/overlays/BaseOverlay.vue';
import AddSuggestionOverlay from '@/views/meetings/AddSuggestionOverlay.vue';
import { IMeeting, ISuggestion } from '@/stores/meetings';
import Tts from '@/components/shared/Tts.vue';
import { useI18n } from 'vue-i18n';

const emit = defineEmits(['close']);

const showAddSuggestionOverlay = ref<boolean>(false);

interface Props {
  suggestions: ISuggestion[];
  meeting: IMeeting;
}

const props = defineProps<Props>();

const { t } = useI18n();

const ttsSuggestionContent = computed(() => {
  return [
    t('meeting.suggestions'),
    t('meeting.suggestionItems', props.suggestions.length),
  ].join('. ');
});
</script>

<template>
  <BaseOverlay @close="emit('close')" :title="$t('meeting.suggestions')">
    <template #tts>
      <Tts contentClassName="tts-suggestion-content" />
    </template>
    <template #content>
      <div class="tts-suggestion-content">
        <div class="sr-only" :aria-label="ttsSuggestionContent"></div>

        <div
          v-for="(suggestion, index) in suggestions"
          :key="suggestion.id"
          class="participants content inline-flex w-full mb-4 items-center"
        >
          <img
            :class="{ '-m-1': index !== 0 }"
            class="rounded-full border-2 border-neutral-0 mr-1 shadow-xsm"
            :style="{ width: '84px', height: '84px' }"
            v-optimized="{ src: suggestion.suggester.displayImage }"
          />

          <div
            class="w-full border border-neutral-300 relative px-4 py-6 -mt-1 ml-1 bg-neutral-0 shadow-smallBottom rounded-2xl"
          >
            <p class="h600">
              {{ suggestion.suggester.displayName }}
            </p>
            <p
              class="b500"
              :aria-label="
                $t('meeting.tts_user_suggestion', {
                  user: suggestion.suggester.displayName,
                  suggestion: suggestion.title,
                })
              "
            >
              {{ suggestion.title }}
            </p>
          </div>
        </div>

        <div
          v-if="!suggestions.length"
          class="w-full flex flex-col items-center pt-24"
        >
          <fa-icon
            :icon="['fas', 'fa-lightbulb']"
            class="text-6xl text-neutral-300 mb-4"
          />
          <p class="b600">
            {{ $t('meeting.noSuggestionsYet') }}
          </p>
        </div>
      </div>
    </template>

    <template #actions>
      <ButtonEl
        class="bold"
        text="meeting.writeSuggestion"
        :icon="['fal', 'fa-edit']"
        background="bg-darkOcean-600"
        textColor="text-neutral-0"
        rounded="full"
        padding="px-16"
        @click="showAddSuggestionOverlay = true"
      />
    </template>
  </BaseOverlay>

  <AddSuggestionOverlay
    v-if="showAddSuggestionOverlay"
    :meeting="props.meeting"
    @close="showAddSuggestionOverlay = false"
  />
</template>

<style lang="scss">
.suggestions {
  min-height: 1200px;
}
.meetingActions {
  bottom: 150px;
}

.write-suggestion {
  bottom: 150px;
}
</style>
